import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch } from "@fortawesome/free-solid-svg-icons";
import { FaEye } from "react-icons/fa";
import axios from "axios";
import { DataGrid } from "@mui/x-data-grid";
import { toast } from "react-toastify";
import Grid from '../components/Grid';
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { FaDownload } from "react-icons/fa";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function TransferOrderReceipts() {
  const [receipts, setReceipts] = useState([]);
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedSearchField, setSelectedSearchField] = useState("transferOrderCode");

  useEffect(() => {
    const fetchReceipts = async () => {
      try {
        const token = sessionStorage.getItem("token");
        if (!token) {
          toast.error("Authentication token is missing. Please log in again.");
          return;
        }

        const response = await axios.get(`${API_BASE_URL}/transferOrderReceipts`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const receipts = response.data.transferOrderReceipts || [];

        const formattedReceipts = receipts.map((receipt) => ({
          id: receipt._id,
          transferOrderReceiptCode: receipt.transfer_order_receipt_code || "N/A",
          fromLocation: receipt.from_location?.location_name || "Unknown",
          toLocation: receipt.to_location?.location_name || "Unknown",
          receiptDate: new Date(receipt.receipt_date).toLocaleDateString("en-GB"),
          createdBy: receipt.created_by?.username || "Unknown",
          transportOrderCode: receipt.transport_order_id || "N/A",
          createdAt: receipt.createdAt,
          transferOrderCode: receipt.transfer_order_id || "N/A",
          items: receipt.items || [],
          originalReceipt: receipt,
        }));

        setReceipts(formattedReceipts);
      } catch (error) {
        console.error("Error fetching receipts:", error);
        toast.error("Error fetching transfer order receipts.");
      }
    };

    fetchReceipts();
  }, []);

  const handleReceiptClick = async (receipt) => {
    try {
      const token = sessionStorage.getItem("token");
      if (!token) {
        toast.error("Authentication token is missing. Please log in again.");
        return;
      }
  
      const response = await axios.get(
        `${API_BASE_URL}/transferOrderReceipts/${receipt.id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
  
      const detailedReceipt = response.data.transferOrderReceipt;
  
      // Check if the detailed receipt exists and has items
      if (!detailedReceipt || !Array.isArray(detailedReceipt.items) || detailedReceipt.items.length === 0) {
        toast.error("No items found for this transfer order receipt.");
        return;
      }
  
      // Safely access location names and use fallback if needed
      const sourceLocation = detailedReceipt.from_location?.location_name || "Unknown";
      const destinationLocation = detailedReceipt.to_location?.location_name || "Unknown";
  
      // Map formatted products
      const formattedProducts = detailedReceipt.items.map((item) => ({
        id: item.product_id._id,
        product_name: item.product_id.product_name,
        product_image: item.product_id.product_image,
        product_code: item.product_id.product_code,
        quantity: item.qty,
        unit: item.product_id.unit?.unit_name,
      }));
  

      setSelectedReceipt({
        ...detailedReceipt,
        products: formattedProducts,
        sourceLocation: sourceLocation,
        destinationLocation: destinationLocation,
        selectedProducts: formattedProducts || [],
      });
  
    } catch (error) {
      console.error("Error fetching transfer order details:", error.message);
      toast.error("Error fetching transfer order details.");
    }
  };  

  const handleClosePopup = () => {
    setSelectedReceipt(null);
  };

  const sortedTransferOrderReceipts = [...receipts].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt) );

  const filteredReceipts = sortedTransferOrderReceipts.filter((receipt) =>
    receipt.transferOrderReceiptCode?.toLowerCase().includes(searchTerm.toLowerCase())
    || receipt.fromLocation?.toLowerCase().includes(searchTerm.toLowerCase())
    || receipt.toLocation?.toLowerCase().includes(searchTerm.toLowerCase())
    || receipt.receiptDate?.toLowerCase().includes(searchTerm.toLowerCase())
    || receipt.createdBy?.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const handleDownloadExcel = async () => {
  const dataToExport =
    searchTerm.trim().length > 0 ? filteredReceipts : receipts;

    if (dataToExport.length === 0) {
      toast.warn("No transfer order receipt data to export.");
      return;
    }

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Transfer Order Receipts");

    const headerRow = [
      "Receipt Code",
      "From Location",
      "To Location",
      "Receipt Date",
      "Created By",
      "Transfer Order Code",
      "Transport Order Code",
    ];
    worksheet.addRow(headerRow);

    const header = worksheet.getRow(1);
    header.eachCell((cell) => {
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFD966" }, // Light yellow
      };
      cell.font = { bold: true };
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };
    });

    dataToExport.forEach((receipt) => {
      worksheet.addRow([
        receipt.transferOrderReceiptCode || "N/A",
        receipt.fromLocation || "Unknown",
        receipt.toLocation || "Unknown",
        receipt.receiptDate || "N/A",
        receipt.createdBy || "Unknown",
        typeof receipt.transferOrderCode === 'string' ? receipt.transferOrderCode : receipt.transferOrderCode?.transfer_order_code || "N/A",
        typeof receipt.transportOrderCode === 'string' ? receipt.transportOrderCode : receipt.transportOrderCode?.transport_order_code || "N/A",
      ]);
    });

    worksheet.columns.forEach((column) => {
      column.width = column.header
        ? column.header.toString().length < 25
          ? 25
          : column.header.toString().length
        : 25;
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(blob, "transfer_order_receipts.xlsx");
  };

  const columns = [
    { field: "transferOrderReceiptCode", headerName: "Receipt Code", flex: 1 },
    { field: "receiptDate", headerName: "Receipt Date", flex: 1 },
    { field: "fromLocation", headerName: "Source Location", flex: 1 },
    { field: "toLocation", headerName: "Destination Location", flex: 1 },
    { field: "createdBy", headerName: "Created By", flex: 1 },
    {
      field: "action",
      headerName: "Action",
      flex: 1,
      renderCell: (params) => (
        <button
          onClick={() => handleReceiptClick(params.row)}
          className="bg-yellow-500 text-white p-2 flex items-center justify-center rounded"
        >
          <FaEye />
        </button>
      ),
    },
  ];

  return (
    <div className="p-8">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-3xl font-bold text-green-700">Transfer Order Receipts</h1>
      </div>

      <div className="bg-white p-4 rounded-lg shadow mb-4">
        <div className="flex justify-between mb-4 items-center flex-wrap gap-4">
            {/* Left Section: Search filters */}
            <div className="flex items-center flex-wrap gap-4">
              <select
                className="px-4 py-2 border border-gray-300 rounded-lg"
                value={selectedSearchField}
                onChange={(e) => setSelectedSearchField(e.target.value)}
              >
                <option value="transferOrderCode">Transfer Order Code</option>
                <option value="fromLocation">Source Location</option>
                <option value="toLocation">Destination Location</option>
                <option value="receiptDate">Receipt Date</option>
                <option value="createdBy">Created By</option>
              </select>

              <div className="relative">
                <input
                  type="text"
                  placeholder="Search Receipts"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="px-4 py-2 pl-10 border border-gray-300 rounded-lg w-full max-w-xs"
                />
                <div className="absolute inset-y-0 left-0 flex items-center pl-3">
                  <FontAwesomeIcon icon={faSearch} className="text-gray-500" />
                </div>
              </div>
            </div>

            {/* Right Section: Download Excel */}
            <div>
              <button
                onClick={handleDownloadExcel}
                className="flex items-center bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
              >
                <FaDownload className="mr-2" />
                Download Excel
              </button>
            </div>
          </div>        
        <Grid rows={filteredReceipts} columns={columns} pageSize={5} getRowId={(row) => row.transferOrderReceiptCode}/>
      </div>

      {selectedReceipt && (
        <div className="fixed inset-0 flex items-center justify-center bg-gray-900 bg-opacity-50 z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg w-full md:w-2/3 lg:w-1/2 relative max-h-[90vh] overflow-y-auto">
            <button
              onClick={handleClosePopup}
              className="absolute top-2 right-2 bg-red-500 text-white w-8 h-8 flex justify-center items-center rounded"
            >
              &times;
            </button>
            <h2 className="text-xl font-semibold mb-4">Receipt: {selectedReceipt.transfer_order_receipt_code}</h2>
            <div className="flex flex-row justify-evenly space-x-4">
                <div className="mb-4 w-full px-1">
                    <label className="block text-gray-700 font-bold">From</label>
                    <input
                    type="text"
                    value={selectedReceipt.from_location?.location_name || ""}
                    readOnly
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    />
                </div>
                <div className="mb-4 w-full px-1">
                    <label className="block text-gray-700 font-bold">TO</label>
                    <input
                    type="text"
                    value={selectedReceipt.to_location?.location_name || ""}
                    readOnly
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    />
                </div> 
            </div>
            <div className="flex flex-row justify-evenly space-x-4">
                <div className="mb-4 w-full px-1">
                    <label className="block text-gray-700 font-bold">Transfer Order Code:</label>
                    <input
                    type="text"
                    value={selectedReceipt.transfer_order_id || ""}
                    readOnly
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    />
                </div>
                <div className="mb-4 w-full px-1">
                    <label className="block text-gray-700 font-bold">Transport Order Code:</label>
                    <input
                    type="text"
                    value={selectedReceipt.transport_order_id || ""}
                    readOnly
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    />
                </div>  
            </div>
            <div className="flex flex-row justify-evenly space-x-4">
                <div className="mb-4 w-full px-1">
                    <label className="block text-gray-700 font-bold">Created By</label>
                    <input
                    type="text"
                    value={selectedReceipt.created_by?.username || ""}
                    readOnly
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    />
                </div>
                <div className="mb-4 w-full px-1">
                    <label className="block text-gray-700 font-bold">Date</label>
                    <input
                    type="text"
                    value={new Date(selectedReceipt.receipt_date).toLocaleDateString("en-GB") || ""}
                    readOnly
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    />
                </div>
            </div>
            <h3 className="text-lg font-semibold mt-4">Items</h3>
            <div className="overflow-auto max-h-60">
              <DataGrid
                rows={selectedReceipt?.products || []} // Use the products from selectedProductionBatch
                columns={[
                    { field: "product_code", headerName: "ID", width: 200 },
                    {
                      field: "product_image",
                      headerName: "Image",
                      width: 150,
                      renderCell: (params) =>
                        params.row.product_image ? (
                            <img
                                src={`${process.env.REACT_APP_IMAGE_BASE_URL}${params.row.product_image}`}
                                alt="Product"
                                className="h-16 w-16 object-cover rounded-md"
                            />
                        ) : (
                        "No Image"
                    ),
                    },
                    {
                        field: "product_name",
                        headerName: "Product Name",
                        width: 150,
                    },
                    {
                        field: "quantity",
                        headerName: "Quantity",
                        width: 150,
                        headerAlign: "right",
                        align: "right"
                    },
                    { 
                          field: "unit", 
                          headerName: "Units", 
                          width: 150, 
                          headerAlign: "center", 
                          align: "center" 
                        },
                    ]}
                    pageSize={5}
                />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TransferOrderReceipts;
